import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ColumnDef } from '@tanstack/react-table';

import { FundGroupDtoIn } from '@/models/in/FundGroupDtoIn';
import DataTable from '@/components/ui/DataTable/DataTable';
import { Col } from '@/components/ui/DataTable/ColumnsHelpers';
import { useGetAuthApi } from '@/hooks/UseApiCalls';
import Button from '@/components/ui/Button';
import useTableDensity from '@/hooks/useTableDensity';
import EditOrCreateFundGroupDialog from './EditOrCreateFundGroupDialog';
import DeleteFundGroupDialog from './DeleteFundGroupDialog';

const QUERY_URL = 'management/fund-groups';
const DENSITY_STORAGE_KEY = 'table.density.fundGroups';

const FundGroupsManagementPage = () => {
  const { t } = useTranslation();

  const [searchQuery, setSearchQuery] = useState('');
  const [density, setDensity] = useTableDensity(DENSITY_STORAGE_KEY, 'normal');
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [groupToEdit, setGroupToEdit] = useState<FundGroupDtoIn | null>(null);
  const [groupToDelete, setGroupToDelete] = useState<FundGroupDtoIn | null>(null);

  const { data: groups, isLoading, refetch } = useGetAuthApi<FundGroupDtoIn[]>(QUERY_URL);

  const openCreate = useCallback(() => { setGroupToEdit(null); setIsEditOpen(true); }, []);
  const openEdit = useCallback((g: FundGroupDtoIn) => { setGroupToEdit(g); setIsEditOpen(true); }, []);
  const closeEdit = useCallback(() => { setIsEditOpen(false); setGroupToEdit(null); }, []);

  const columns: ColumnDef<FundGroupDtoIn>[] = [
    {
      id: 'name',
      header: Col(t('fundGroups.columns.name')),
      accessorKey: 'name',
      enableSorting: true,
      meta: { className: 'min-w-[200px]' },
    },
    {
      id: 'description',
      header: Col(t('fundGroups.columns.description')),
      accessorKey: 'description',
      enableSorting: false,
      meta: { className: 'min-w-[220px]' },
    },
    {
      id: 'members',
      header: Col(t('fundGroups.columns.members')),
      enableSorting: true,
      accessorKey: 'membersCount',
      cell: ({ row }) => (
        <span className="body-sm-regular">
          {t('fundGroups.membersBreakdown', { funds: row.original.fundIds.length, users: row.original.userEmails.length })}
        </span>
      ),
      meta: { className: 'min-w-[180px]' },
    },
    {
      id: 'usedBy',
      header: Col(t('fundGroups.columns.usedBy')),
      accessorKey: 'usedByFeaturesCount',
      enableSorting: true,
      meta: { className: 'min-w-[140px]' },
    },
    {
      id: 'actions',
      header: Col(t('fundGroups.columns.actions')),
      enableSorting: false,
      cell: ({ row }) => (
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => openEdit(row.original)}>{t('fundGroups.edit')}</Button>
          <Button variant="outline" className="!text-red-500 !border-red-300" onClick={() => setGroupToDelete(row.original)}>
            {t('fundGroups.delete')}
          </Button>
        </div>
      ),
      meta: { className: 'min-w-[200px]', lockLayout: true },
    },
  ];

  return (
    <div className="w-full">
      <DataTable
        title={t('fundGroups.title')}
        data={groups ?? []}
        isLoading={isLoading}
        columns={columns}
        filters={
          <Button variant="primary" onClick={openCreate}>{t('fundGroups.addGroup')}</Button>
        }
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        density={density}
        onDensityChange={setDensity}
        columnLayoutKey="fundGroups"
        searchPlaceholder="fundGroups.search"
        customGlobalSearch={() => true}
      />

      {isEditOpen && (
        <EditOrCreateFundGroupDialog open={isEditOpen} onClose={closeEdit} groupToEdit={groupToEdit} onSuccess={refetch} />
      )}

      {groupToDelete && (
        <DeleteFundGroupDialog open={!!groupToDelete} group={groupToDelete} onClose={() => setGroupToDelete(null)} onSuccess={refetch} />
      )}
    </div>
  );
};

export default FundGroupsManagementPage;
